import type { Invoice } from '../Types/invoicesTypes';
import { InvoiceType } from '../Types/invoicesTypes';
import { invoiceLogger } from './invoiceLogger';
import {
  emitSettlementBreakdownTelemetry,
  type SettlementBreakdownTelemetryEventType,
} from './invoiceSettlementTelemetry';

const BREAKDOWN_FIELDS = [
  'prepaymentsSettledAmount',
  'importedReservationDirectAmount',
  'invoicePaymentsNetAmount',
] as const;

type BreakdownField = (typeof BREAKDOWN_FIELDS)[number];

const hasFiniteNumber = (value: unknown): value is number =>
  typeof value === 'number' && Number.isFinite(value);

const hasBreakdownField = (invoice: Invoice, field: BreakdownField): boolean =>
  hasFiniteNumber(invoice[field]);

const hasAllBreakdownFields = (invoice: Invoice): boolean =>
  BREAKDOWN_FIELDS.every((field) => hasBreakdownField(invoice, field));

const hasAnyBreakdownField = (invoice: Invoice): boolean =>
  BREAKDOWN_FIELDS.some((field) => hasBreakdownField(invoice, field));

const hasPaymentHistory = (invoice: Invoice): boolean => {
  if (typeof invoice.hasPaymentHistory === 'boolean') {
    return invoice.hasPaymentHistory;
  }

  if (hasFiniteNumber(invoice.invoicePaymentsNetAmount)) {
    return invoice.invoicePaymentsNetAmount !== 0;
  }

  return (invoice.paidAmount ?? 0) > 0;
};

const withBreakdownDefaults = (invoice: Invoice): Invoice => ({
  ...invoice,
  prepaymentsSettledAmount: invoice.prepaymentsSettledAmount ?? 0,
  importedReservationDirectAmount: invoice.importedReservationDirectAmount ?? 0,
  invoicePaymentsNetAmount: invoice.invoicePaymentsNetAmount ?? 0,
});

export class SettlementBreakdownContractError extends Error {
  constructor(
    public readonly invoiceId: number | undefined,
    public readonly missingFields: BreakdownField[]
  ) {
    super(
      `Settlement breakdown missing required fields for invoice ${invoiceId ?? 'unknown'}: ${missingFields.join(', ')}`
    );
    this.name = 'SettlementBreakdownContractError';
  }
}

export const withSettlementBreakdownDefaults = (invoice: Invoice): Invoice => {
  if (invoice.type !== InvoiceType.Final) {
    return withBreakdownDefaults(invoice);
  }

  const hasPaymentHistoryFlag = hasPaymentHistory(invoice);
  const hasAnyBreakdown = hasAnyBreakdownField(invoice);

  if (hasAllBreakdownFields(invoice)) {
    return withBreakdownDefaults(invoice);
  }

  if (!hasAnyBreakdown) {
    const canUseLegacyFallback = invoice.isLegacySettlementRecord === true && !hasPaymentHistoryFlag;

    if (canUseLegacyFallback) {
      invoiceLogger.warn('[Invoices] Legacy settlement fallback applied (missing breakdown)', {
        invoiceId: invoice.id,
        invoiceNumber: invoice.invoiceNumber,
      });
      emitSettlementBreakdownTelemetry({
        eventType: 'legacy_fallback_applied',
        invoiceId: invoice.id,
        invoiceNumber: invoice.invoiceNumber,
        isLegacySettlementRecord: invoice.isLegacySettlementRecord,
        hasPaymentHistory: hasPaymentHistoryFlag,
        missingFields: [...BREAKDOWN_FIELDS],
      });
      return withBreakdownDefaults(invoice);
    }
  }

  const missingFields = BREAKDOWN_FIELDS.filter((field) => !hasBreakdownField(invoice, field));
  const blockedEventType: SettlementBreakdownTelemetryEventType = hasAnyBreakdown
    ? 'partial_breakdown_blocked'
    : invoice.isLegacySettlementRecord === true
      ? 'legacy_missing_breakdown_with_history_blocked'
      : 'non_legacy_missing_breakdown_blocked';

  emitSettlementBreakdownTelemetry({
    eventType: blockedEventType,
    invoiceId: invoice.id,
    invoiceNumber: invoice.invoiceNumber,
    isLegacySettlementRecord: invoice.isLegacySettlementRecord,
    hasPaymentHistory: hasPaymentHistoryFlag,
    missingFields,
  });

  invoiceLogger.error('[Invoices] Blocking invoice read due to missing settlement breakdown', {
    invoiceId: invoice.id,
    invoiceNumber: invoice.invoiceNumber,
    missingFields,
    isLegacySettlementRecord: invoice.isLegacySettlementRecord,
    hasPaymentHistory: hasPaymentHistoryFlag,
  });

  throw new SettlementBreakdownContractError(invoice.id, missingFields);
};

export const withSettlementBreakdownDefaultsList = (invoices: Invoice[]): Invoice[] =>
  invoices.map(withSettlementBreakdownDefaults);
